<template>
  <Transition :name="flight.sector.animation" mode="out-in">
    <div class="tw:!max-w-3xl tw:!mx-auto tw:!my-5" v-if="!isItineraryAvailable">
      <div class="tw:!text-center tw:!my-10">
        <p class="tw:!text-xl tw:!font-medium">Flights not found</p>
        <p class="tw:!opacity-80">Please try select different route or departure date.</p>
      </div>
    </div>
    <div class="tw:!max-w-3xl tw:!my-5 tw:!mx-auto" v-else-if="flight.sector.is('departure')">
      <div class="od-title tw:text-center">Pick Departure Flight</div>
      <div>
        <FlightRow 
          v-for="item of flight.departure!.slice(0, pagination.page * pagination.item)"
          :flight="item"
          @select="
            flight.mode === 'oneway'
            ? emit('select', item)
            : [flight.sector.next(), selection = item, scrollCenter()]
          "
        >
          <template #select="{ select, flight }">
            <div class="tw:!content-center">
              <button class="od-button" @click="select(flight)" >Select</button>
            </div>
          </template>
        </FlightRow>
      </div>
      <div v-if="flight.departure!.length > pagination.item * pagination.page" class="tw:!flex tw:!justify-center tw:!mt-3">
        <button class="od-button w-full" raised @click="pagination.page++">
          See more flights
        </button>
      </div>
    </div>

    <div :id="id" v-else-if="flight.sector.is('return')" class="tw:!max-w-3xl tw:!my-5 tw:!mx-auto">
      <div class="od-title tw:!text-center">Pick Return Flight</div>

      <div>
        <FlightRow
          v-for="item of returnFlights!.slice(0, pagination.page * pagination.item)"
          :flight="item"
          @select="emit('select', selection!, item)"
        >
          <template #select="{ select, flight }">
            <div class="tw:!content-center">
              <button class="od-button" @click="select(flight)" >Select</button>
            </div>
          </template>
        </FlightRow>
      </div>

      <div v-if="returnFlights?.length" class="tw:!mt-3">
        <button class="od-button-outline" @click="flight.sector.set('departure')">Back</button>
      </div>

      <div class="tw:!max-w-3xl tw:!mx-auto tw:!my-5" v-if="!returnFlights?.length">
        <div class="tw:!text-center tw:!my-10">
          <p class="tw:!text-xl tw:!font-medium">
            Return flight not possible with {{ selection?.company.marketing.name ?? 'your selection' }}
          </p>

          <button class="od-button tw:!mt-3" @click="flight.sector.set('departure')">Select Again</button>
        </div>
      </div>

      <div v-if="returnFlights!.length > pagination.item * pagination.page" class="tw:!flex tw:!justify-center tw:!mt-3">
        <button class="od-button" raised @click="pagination.page++">
          See more flights
        </button>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { computed, reactive, ref, shallowReactive, useId } from 'vue'
import { useRotateKeyTransition } from '~/composables/useTransition'
import type { IFlight } from '~/types'
import FlightRow from './FlightRow.vue'

const id = useId()

const pagination = reactive({ page: 1, item: 7 })
const selection = ref<IFlight>()

const flight = shallowReactive({
  mode: 'oneway' as 'oneway'|'roundtrip',

  /** Determine which flight departure or returning user are currently selecting */
  sector: reactive(useRotateKeyTransition(['departure', 'return'])),

  /** Flight list */
  departure: undefined as Array<IFlight>|undefined,

  /** Flight list */
  returning: undefined as Array<IFlight>|undefined,
})

const returnFlights = computed(() => {
  if (! selection.value || !flight.returning?.length) return

  const flights = selection.value.connecting?.flights
    ? flight.returning.filter(item => selection.value?.connecting?.flights?.includes(item.id))
    : flight.returning!.filter(item => item.company.marketing.code === selection.value!.company.marketing.code)

  return flights
})

const isItineraryAvailable = computed(() => {
  if (! flight.departure) {
    return false
  }

  if (flight.mode === 'roundtrip' && !flight.returning) {
    return false
  }

  return flight.mode === 'oneway'
    ? flight.departure.length > 0
    : flight.departure.length > 0 && +(flight.returning?.length ?? 0) > 0
})

const set = (departure: IFlight[], returning?: IFlight[]) => {
  flight.mode = returning ? 'roundtrip' : 'oneway'

  flight.departure = departure
  flight.returning = returning

  flight.sector.set('departure')

  pagination.page = 1
}

const scrollCenter = () => {
  setTimeout(() => {
    const e = document.querySelector<HTMLDivElement>(`#${id}`)
    e && e.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, 500)
}

const goToFistSection = () => {
  flight.sector.set('departure')
}

defineExpose({ set, goToFistSection })

const emit = defineEmits<{ select: [IFlight, IFlight?], preSelect: [IFlight] }>()
</script>